/**
 * Research page: browse registered companies, register new ones, and see
 * each company's latest research score at a glance before drilling into
 * the full report on the Company Report page.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/api/client";
import type { Company, ResearchReport } from "@/types";
import ScoreBadge from "@/components/research/ScoreBadge";
import DataSourcesStatus from "@/components/research/DataSourcesStatus";
import SyncTickerPanel from "@/components/research/SyncTickerPanel";

export default function Research(): React.ReactElement {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [latestByCompany, setLatestByCompany] = useState<Record<string, ResearchReport | null>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [symbol, setSymbol] = useState("");
  const [name, setName] = useState("");
  const [sector, setSector] = useState("");
  const [industry, setIndustry] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadCompanies = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<Company[]>("/companies");
      setCompanies(data);

      const reportEntries = await Promise.all(
        data.map(async (company) => {
          try {
            const response = await apiClient.get<ResearchReport>(
              `/companies/${company.id}/research/latest`
            );
            return [company.id, response.data] as const;
          } catch {
            return [company.id, null] as const;
          }
        })
      );
      setLatestByCompany(Object.fromEntries(reportEntries));
    } catch {
      setError("Unable to load companies.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadCompanies();
  }, []);

  const handleCreateCompany = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await apiClient.post("/companies", {
        symbol,
        name,
        sector: sector || null,
        industry: industry || null,
      });
      setSymbol("");
      setName("");
      setSector("");
      setIndustry("");
      setShowForm(false);
      await loadCompanies();
    } catch {
      setError("Unable to register company. Check the symbol isn't already in use.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Research</h1>
          <p className="mt-1 text-sm text-mist-400">
            Institutional-grade research across fundamentals, technicals, news, and macro context.
          </p>
        </div>
        <button className="btn-secondary" onClick={() => setShowForm((prev) => !prev)}>
          {showForm ? "Cancel" : "Add manually"}
        </button>
      </div>

      <DataSourcesStatus />

      <SyncTickerPanel onSynced={() => void loadCompanies()} />

      {showForm && (
        <form onSubmit={handleCreateCompany} className="panel grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          <div>
            <label className="field-label">Symbol</label>
            <input
              required
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              className="field-input"
              placeholder="AAPL"
            />
          </div>
          <div>
            <label className="field-label">Name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="field-input"
              placeholder="Apple Inc."
            />
          </div>
          <div>
            <label className="field-label">Sector</label>
            <input
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              className="field-input"
              placeholder="Technology"
            />
          </div>
          <div>
            <label className="field-label">Industry</label>
            <input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="field-input"
              placeholder="Consumer Electronics"
            />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={isSubmitting} className="btn-primary w-full sm:w-auto">
              {isSubmitting ? "Registering…" : "Register"}
            </button>
          </div>
        </form>
      )}

      {error && <p className="text-sm text-signal-rose">{error}</p>}

      {isLoading ? (
        <p className="text-sm text-mist-400">Loading companies…</p>
      ) : companies.length === 0 ? (
        <div className="panel flex flex-col items-center gap-2 py-16 text-center">
          <p className="text-sm font-medium text-mist-200">No companies registered yet</p>
          <p className="text-sm text-mist-400">Register a company above to start building research on it.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {companies.map((company) => {
            const report = latestByCompany[company.id];
            return (
              <Link
                key={company.id}
                to={`/research/${company.id}`}
                className="panel flex flex-col gap-3 p-5 transition hover:border-ink-500"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-sm font-semibold text-signal-teal">{company.symbol}</p>
                    <p className="text-sm text-mist-200">{company.name}</p>
                  </div>
                  {report ? (
                    <ScoreBadge score={report.overall_score} />
                  ) : (
                    <span className="rounded-full border border-ink-600 px-2 py-0.5 text-xs text-mist-400">
                      No report
                    </span>
                  )}
                </div>
                <p className="text-xs text-mist-400">
                  {company.sector ?? "Sector unclassified"}
                  {company.industry ? ` · ${company.industry}` : ""}
                </p>
                {report && (
                  <p className="text-xs text-mist-400">
                    Confidence {Number(report.confidence_score).toFixed(0)}%
                  </p>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
